import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ErrorNote, Spinner } from '../components/ui';

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<{ valid: boolean; email?: string; purpose?: string } | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState({ valid: false }); return; }
    api.get<{ valid: boolean; email?: string; purpose?: string }>(`/auth/setup-token/${encodeURIComponent(token)}`)
      .then(setState)
      .catch(() => setState({ valid: false }));
  }, [token]);

  if (!state) return <Spinner label="Checking your link" />;

  if (!state.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="card w-full max-w-sm p-6 text-center">
          <h1 className="text-lg font-semibold">That link has expired</h1>
          <p className="mt-2 text-sm text-slate-600">
            Setup links last 72 hours and reset links one hour, and each can be used once.
            Ask an administrator to send a new one.
          </p>
          <button className="btn-secondary mt-4" onClick={() => navigate('/login')}>Back to sign in</button>
        </div>
      </div>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError('Those passwords do not match'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/set-password', { token, password });
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set the password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-sm p-6">
        <h1 className="text-xl font-semibold">
          {state.purpose === 'reset' ? 'Choose a new password' : 'Welcome to D2P Expenses'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{state.email}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="label" htmlFor="pw">New password</label>
            <input id="pw" type="password" className="field" autoComplete="new-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} required />
            <p className="mt-1 text-xs text-slate-500">
              At least 10 characters, with an uppercase letter, a lowercase letter and a digit.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="pw2">Confirm password</label>
            <input id="pw2" type="password" className="field" autoComplete="new-password"
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <ErrorNote>{error}</ErrorNote>
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </form>
      </div>
    </div>
  );
}
